import React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { baseUrl } from "../config";

// Helper function to extract filename and size
const extractFilenameAndSize = (url) => {
  const filename = url.substring(url.lastIndexOf("/") + 1);
  const sizeMatch = filename.match(/_(\d+x\d+)_/);
  const size = sizeMatch ? sizeMatch[1] : null;
  return { filename, size };
};

// Function to extract size from Windows icon filename
const extractSizeFromFilename = (filename) => {
  const pattern = /windows-phone-tile_(large|medium|small|wide)_(.+)/;
  const match = filename.match(pattern);

  const sizeMapping = {
    small: "70x70",
    medium: "150x150",
    wide: "310x150",
    large: "310x310",
  };

  return match ? { size: sizeMapping[match[1]] || null } : { size: null };
};

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  facebook,
  twitter,
  whatsapp,
  linkedin,
  teams,
  type = "website",
  author = "GeniusNexa",
  publishedTime,
  modifiedTime,
  section,
  tags,
  locale = "en_US",
  alternateLocales = [],
  structuredData,
  robotsIndex = true,
  robotsFollow = true,
  androidicons = `${baseUrl}/icons/icon-192x192.png,${baseUrl}/icons/icon-512x512.png`,
  appletouchicon = `${baseUrl}/images/logo-icon.svg`,
  windowsicon = `${baseUrl}/images/logo-icon.svg`,
}) => {
  const appleIcons = appletouchicon.split(",").map((icon) => icon.trim());
  const androidIcons = androidicons.split(",").map((icon) => icon.trim());
  const windowsIcons = windowsicon.split(",").map((icon) => icon.trim());

  return (
    <HelmetProvider>
      <Helmet>
        {/* General Meta Tags */}
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        <meta name="author" content={author} />
        <meta
          name="robots"
          content={`${robotsIndex ? "index" : "noindex"},${
            robotsFollow ? "follow" : "nofollow"
          }`}
        />
        <meta
          name="googlebot"
          content={`${robotsIndex ? "index" : "noindex"},${
            robotsFollow ? "follow" : "nofollow"
          }`}
        />
        <meta
          name="bingbot"
          content={`${robotsIndex ? "index" : "noindex"},${
            robotsFollow ? "follow" : "nofollow"
          }`}
        />
        <link rel="canonical" href={url} />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="format-detection" content="telephone=no" />

        {/* OpenGraph Meta Tags */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={url} />
        <meta property="og:site_name" content="GeniusNexa" />
        <meta property="og:locale" content={locale} />
        {alternateLocales.map((altLocale, idx) => (
          <meta key={idx} property="og:locale:alternate" content={altLocale} />
        ))}
        {(facebook || image) && (
          <>
            <meta property="og:image" content={facebook || image} />
            <meta property="og:image:secure_url" content={facebook || image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={title} />
            <meta property="og:image:type" content="image/jpeg" />
          </>
        )}
        {type === "article" && (
          <>
            {publishedTime && (
              <meta property="article:published_time" content={publishedTime} />
            )}
            {modifiedTime && (
              <meta property="article:modified_time" content={modifiedTime} />
            )}
            {author && <meta property="article:author" content={author} />}
            {section && <meta property="article:section" content={section} />}
            {tags &&
              tags
                .split(",")
                .map((tag, idx) => (
                  <meta key={idx} property="article:tag" content={tag.trim()} />
                ))}
          </>
        )}

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:site" content="@GeniusNexa" />
        <meta name="twitter:creator" content="@GeniusNexa" />
        {(twitter || image) && (
          <>
            <meta name="twitter:image" content={twitter || image} />
            <meta name="twitter:image:alt" content={title} />
          </>
        )}

        {/* WhatsApp Meta Tags */}
        <meta property="whatsapp:title" content={title} />
        <meta property="whatsapp:description" content={description} />
        {(whatsapp || image) && (
          <meta property="whatsapp:image" content={whatsapp || image} />
        )}

        {/* LinkedIn Meta Tags */}
        <meta property="linkedin:title" content={title} />
        <meta property="linkedin:description" content={description} />
        {(linkedin || image) && (
          <meta property="linkedin:image" content={linkedin || image} />
        )}

        {/* Microsoft Teams Meta Tags */}
        <meta property="teams:title" content={title} />
        <meta property="teams:description" content={description} />
        {(teams || image) && (
          <meta property="teams:image" content={teams || image} />
        )}

        {/* Apple Touch Icons */}
        {appleIcons.map((icon, index) => {
          const { size } = extractFilenameAndSize(icon);
          return size ? (
            <link key={index} rel="apple-touch-icon" sizes={size} href={icon} />
          ) : null;
        })}

        <meta name="msapplication-TileColor" content="#000000" />

        {/* Windows Icons */}
        {windowsIcons.map((icon, index) => {
          const { size } = extractSizeFromFilename(icon);
          return size ? (
            <meta
              key={index}
              name={`msapplication-square${size}logo`}
              content={icon}
            />
          ) : null;
        })}

        {/* Android Icons */}
        {androidIcons.map((icon, index) => {
          const size = icon.match(/(\d+x\d+)/)?.[0];
          return size ? (
            <link key={index} rel="icon" sizes={size} href={icon} />
          ) : null;
        })}

        {/* Structured Data (JSON-LD) */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}

        {/* Preconnect to external domains for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      </Helmet>
    </HelmetProvider>
  );
};

export default SEO;
